import { isPending } from '@fleetcore/shared'
import { BarChart3 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { useAuth } from '../../auth/use-auth'
import { MetricTile } from '../../components/metric-tile'
import { PageHeader } from '../../components/page-header'
import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import type { DashboardTile, FleetHomeView } from './api-types'
import { DashboardErrorState, DegradedNotice, EmptyFleetState, NoTelemetryState } from './empty-states' // prettier-ignore
import { KpiTile } from './kpi-tile'
import { useFleetHome } from './queries'
import { analyticsPath } from './routes'
import { orderTiles, periodOfTile, presentationFor, TILE_ORDER, UNKNOWN_TILE_ICON } from './tiles'
import { WorstDriversTile } from './worst-drivers-tile'

/**
 * The fleet home — spec §8.8.
 *
 * ─── EVERY TILE IS A DOOR (RULE 9) ───────────────────────────────────────────
 *
 * Nothing on this page is decoration. Each tile is an anchor into the module that owns
 * its question, using the route the API itself shipped — see `KpiTile`. A figure with
 * no way through to its evidence is unusable the moment somebody disbelieves it.
 *
 * ─── AND NOTHING HERE IS COMPUTED (RULE 3 OF THE API NOTES) ──────────────────
 *
 * Not one number on this screen is derived in the browser. The counts come from the
 * Redis live projection, the volumes from the continuous aggregates, the leaderboard
 * from the scoring service — the same calls the map, the analytics page and the
 * classement make. The moment this page starts summing tiles to make a "total fleet"
 * figure it can disagree with the screen it links into, and the first time "12 véhicules
 * en mouvement" contradicts eleven markers on the map, the customer stops believing
 * both. The machine and road segments arrive as SEPARATE tiles for the same reason, and
 * are never added together: an excavator's engine hours and a van's kilometres do not
 * sum to anything.
 */
export function DashboardPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const format = useFormatters()
  const { data, isPending: isLoading, isError, error, refetch } = useFleetHome()

  const firstName = (user?.displayName || user?.email || '').split(/[\s@]/)[0] ?? ''
  const tiles = data === undefined ? [] : orderTiles(data.tiles)

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={firstName ? t('dashboard:greeting', { name: firstName }) : t('dashboard:title')}
        description={
          data === undefined
            ? t('dashboard:subtitle')
            : t('dashboard:generatedAt', { time: format.time(data.generatedAt) })
        }
        action={
          <Link
            to={analyticsPath({ period: 'month' })}
            className={cn(
              'inline-flex h-9 items-center gap-2 rounded-md border border-border bg-surface px-3.5',
              'text-sm font-medium text-foreground transition-colors hover:bg-surface-hover',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            )}
          >
            <BarChart3 aria-hidden className="h-4 w-4" />
            {t('dashboard:openAnalytics')}
          </Link>
        }
      />

      {isError ? <DashboardErrorState error={error} onRetry={() => void refetch()} /> : null}

      {data === undefined ? null : <DegradedNotice degraded={data.degraded} />}

      <section aria-label={t('dashboard:sections.fleetStatus')}>
        {isLoading ? (
          <LoadingTiles />
        ) : data === undefined ? null : (
          <TileGrid data={data} tiles={tiles} />
        )}
      </section>

      {data === undefined ? null : <FleetStateExplanation data={data} tiles={tiles} />}
    </div>
  )
}

function TileGrid({
  data,
  tiles,
}: {
  readonly data: FleetHomeView
  readonly tiles: readonly DashboardTile[]
}) {
  const format = useFormatters()
  const weekLabel = `${format.date(data.week.from)} — ${format.date(data.week.to)}`

  // Two per row even on a phone: one full-width tile per number made the home page a
  // scroll of several screens to read a handful of figures.
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
      {tiles.map((tile) =>
        tile.kind === 'list' ? (
          <WorstDriversTile
            key={tile.key}
            tile={tile}
            weekLabel={weekLabel}
            className="col-span-2"
          />
        ) : (
          <KpiTile key={tile.key} tile={tile} period={periodFor(data, tile.key)} />
        ),
      )}
    </div>
  )
}

function periodFor(data: FleetHomeView, key: string) {
  const window = periodOfTile(key)
  if (window === 'today') return data.today
  if (window === 'month') return data.month
  return null
}

/**
 * The tiles before the first response.
 *
 * They are NOT links yet — there is nothing to drill into — and they carry no value at
 * all: `MetricTile` renders skeletons under `aria-busy`, so a screen reader hears
 * "busy" rather than a number the page is about to replace. The keys come from
 * `TILE_ORDER` so the grid does not reflow when the real tiles land.
 */
function LoadingTiles() {
  const { t } = useTranslation()

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
      {TILE_ORDER.map((key) => {
        const presentation = presentationFor(key)
        return (
          <MetricTile
            key={key}
            isLoading
            label={presentation ? t(`dashboard:tiles.${presentation.i18nKey}.label`) : key}
            icon={presentation?.icon ?? UNKNOWN_TILE_ICON}
            metric={{ value: null, availability: 'pending_telemetry' }}
            format={(value) => String(value)}
          />
        )
      })}
    </div>
  )
}

/**
 * Why the page looks the way it does, when it looks empty.
 *
 * Three states, three different next steps, and the order matters: a tenant with no
 * vehicles is not waiting for telemetry, it is waiting for somebody to create a
 * vehicle. Getting that wrong sends an installer to a site where nothing is installed.
 */
function FleetStateExplanation({
  data,
  tiles,
}: {
  readonly data: FleetHomeView
  readonly tiles: readonly DashboardTile[]
}) {
  if (data.fleet.vehicles === 0) {
    return (
      <section className="mt-6">
        <EmptyFleetState />
      </section>
    )
  }

  // Every single tile unmeasured means the platform has heard nothing at all — which is
  // an installation problem, not a quiet day. One measured tile is enough to prove the
  // pipeline works, and then the remaining pending tiles explain themselves.
  const everythingPending = tiles.length > 0 && tiles.every((tile) => isPending(tile.metric))
  if (!everythingPending) return null

  return (
    <section className="mt-6">
      <NoTelemetryState />
    </section>
  )
}
